import {defineStore} from 'pinia'
import {FeatureNs, ScreenNs} from "@shared/types";
import {PublicPageApi} from "@/api/public-page.apis.ts";
import {PublicNs} from "@shared/types/public.namespace.ts";
import {usePublicProjectStore} from "@domains/publish/project/stores/public-project.store.ts";
import {usePPFeatureStore} from "@domains/publish/project/stores/public-feature.store.ts";
import {colors} from "@shared/constants/common.constants.ts";
import ScreenTypeEnums = ScreenNs.ScreenTypeEnums;


export const usePPScreenStore = defineStore("ppScreen", {
    state: () => ({
        screenMap: new Map<string, ScreenNs.Screen>,
        screenTree: [] as ScreenNs.ScreenTreeNode[],
        selectedScreen: {} as ScreenNs.ScreenTreeNode,
        selectedNodeScreenList: [] as ScreenNs.Screen[],

        editingScreen: {} as ScreenNs.Screen,
        screenFeatures: [] as ScreenNs.ScreenFeatures[],

        isScreensCollapsed: false,
        isSearchModeOn: false,
        displayMode: 'GRID',
    }),
    getters: {
        getScreenList: (state) => [...state.screenMap.values()],
        getScreenTree: (state) => state.screenTree,
        getSelectedScreen: (state) => state.selectedScreen,
        getSelectedNodeScreenList: (state) => state.selectedNodeScreenList.sort((a: any, b: any) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime()),
        getEditingScreen: (state) => state.editingScreen,
        getScreenFeatures: (state) => state.screenFeatures,
        getNoSelectedScreenFeatures: (state) => state.screenFeatures.filter((feature: any) => !feature.box).reverse(),
        getSelectedScreenFeatures: (state) => state.screenFeatures.filter((feature: any) => feature.box)

    },
    actions: {
        convertFlatDataToTree() {
            const map = new Map<string, any>;

            this.screenMap.forEach((item: ScreenNs.Screen, key: string) => {
                map.set(item.id, {...item, ...item.properties, children: []} as ScreenNs.ScreenTreeNode);
            });

            const hierarchy: ScreenNs.ScreenTreeNode[] = [];
            this.screenMap.forEach((item: ScreenNs.Screen, key: string) => {
                if (item.parentId) {
                    map.get(item.parentId).children.push(map.get(item.id));
                } else {
                    hierarchy.push(map.get(item.id));
                }
            });

            this.screenTree = hierarchy;
            return hierarchy;
        },


        async loadAllScreen(projectId: string, token: string, password: string): Promise<ScreenNs.Screen[]> {
            const payload = {projectId, token, password} as PublicNs.LoadProjectDataRequest;
            return PublicPageApi.loadScreenData(payload)
                .then((screenList) => {
                    screenList.forEach(item => {
                        if (item.screenType === ScreenTypeEnums.GROUP && (!item.properties?.color)) {
                            item.properties ??= {};
                            item.properties.color = colors[0];
                        }
                        this.screenMap.set(item.id, item);
                    });
                    this.convertFlatDataToTree();
                    return [...this.screenMap.values()];
                })
        },


        getScreenById(screenId: string): ScreenNs.Screen | undefined {
            return this.screenMap.get(screenId);
        },

        selectAScreenById(screenId: string, callback = () => {
        }) {
            const findScreen = this.screenTree.find((item) => item.id == screenId)
            if (findScreen) {
                this.selectAScreen(findScreen, callback);
            } else {
                this.selectAScreen(undefined, callback);
            }
        },

        selectAScreen(selectedItem: ScreenNs.ScreenTreeNode | undefined, callback = () => {
        }) {
            const projectStore = usePublicProjectStore();

            let theScreen: ScreenNs.ScreenTreeNode | undefined = selectedItem;
            if (!theScreen || theScreen.screenType == ScreenNs.ScreenTypeEnums.PROJECT) {
                theScreen = {
                    id: undefined,
                    isIndex: true,
                    summary: projectStore.projectData.name,
                    children: this.screenTree,
                    screenType: ScreenNs.ScreenTypeEnums.PROJECT,
                } as any;
            }

            if (this.selectedScreen !== theScreen) {
                const screenId = theScreen!.id;
                if (screenId) {
                    this.router.push({
                        name: PublicNs.PublicRouteEnums.SCREEN_SELECTED,
                        params: {screenId},
                    });
                } else {
                    this.router.push({
                        name: PublicNs.PublicRouteEnums.SCREEN,
                    });
                }

                this.selectedScreen = theScreen!;
                if (this.selectedScreen.screenType !== 'PROJECT') {
                    projectStore.addItemToRecentlyViewed({type: 'SCREEN', item: this.selectedScreen});
                }

                this.refreshSelectedNodeScreenList();
                callback();
            }
        },

        refreshSelectedScreen() {
            if (this.selectedScreen) {
                if (this.selectedScreen.screenType == ScreenNs.ScreenTypeEnums.PROJECT) {
                    this.selectedScreen.children = this.screenTree;
                } else if (this.selectedScreen.screenType == ScreenNs.ScreenTypeEnums.GROUP) {
                    const findScreen = this.screenTree.find((item) => item.id == this.selectedScreen.id)
                    if (findScreen) {
                        this.selectedScreen = findScreen;
                    } else {
                        this.selectAScreen(undefined);
                    }
                }
                this.refreshSelectedNodeScreenList();
            }
        },

        refreshSelectedNodeScreenList() {
            if (this.selectedScreen) {
                if (this.selectedScreen.screenType !== 'PROJECT') {
                    this.selectedNodeScreenList = [this.selectedScreen]
                } else {
                    const newList: any[] = [];
                    const screenWithoutGroup: any[] = [];
                    this.selectedScreen.children.forEach((item: any) => {
                        if (item.screenType == 'GROUP') {
                            newList.push(item);
                        } else {
                            screenWithoutGroup.push(item);
                        }
                    })

                    newList.push({
                        screenType: ScreenNs.ScreenTypeEnums.GROUP,
                        children: screenWithoutGroup,
                    })

                    this.selectedNodeScreenList = newList
                }
            }
        },

        startEditingScreen(selectedItem: ScreenNs.ScreenTreeNode) {
            this.editingScreen = this.screenMap.get(selectedItem.id)!;
        },

        loadScreenFeatures(screen: ScreenNs.Screen): void {
            if (screen.featureList && screen.featureList.length > 0) {
                const featureStore = usePPFeatureStore();
                let screenFeatures: any = featureStore.getFeatureList.filter((f: FeatureNs.Feature) => screen.featureList.includes(f.id));
                if (screen.boxes?.length >= 0) {
                    screenFeatures = screenFeatures.map((feature: any) => ({
                        ...feature,
                        box: screen.boxes.find((box: any) => box.featureId === feature.id),
                    }));
                }
                this.screenFeatures = screenFeatures;
            } else {
                this.screenFeatures = []
            }
        },

        setDisplayMode(displayMode: 'GRID' | 'LIST'): void {
            this.displayMode = displayMode;
        },
        switchScreensCollapsedValue(): void {
            this.isScreensCollapsed = !this.isScreensCollapsed;
        },
        setSearchMode(isSearchModeOn: boolean): void {
            this.isSearchModeOn = isSearchModeOn;
        },
    },
})
